import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { Customer360Service } from 'src/app/private/services/customer-360.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { environment } from 'src/environments/environment';
import { CustomerIntelligenceResponse } from 'src/app/models/customer-intelligence.model';
import {
  SCENARIOS_BY_TAB,
  SIM_TABS,
  SimScenario,
} from './simulation-scenarios.data';

interface FlowStep {
  num: string;
  title: SafeHtml;
  detail: SafeHtml;
  numStyle: string;
}

interface CardRunState {
  status: 'idle' | 'running' | 'done' | 'error';
  statusText: string;
  flowSteps: FlowStep[];
  payloadJson: string | null;
  response: { ok: boolean; msg: string; ms: number } | null;
  running: boolean;
}

interface CrmFeedEvent {
  icon: string;
  iconClass: string;
  title: string;
  sub: string;
  amount?: string;
  amountCol?: string;
  leadId?: string | number;
  time: string;
}

interface ToastItem {
  icon: string;
  msg: string;
  tag: string;
}

interface SimStats {
  totalSpend: number;
  totalVisits: number;
  totalLeads: number;
  crmEvents: number;
  apiCalls: number;
  apiSuccess: number;
  lastLeadId: string;
  byType: Record<
    'ev' | 'food' | 'mall' | 'gas' | 'charges' | 'partner',
    { count: number; amount: number }
  >;
}

function readUrlQueryParams(): URLSearchParams {
  const chunks: string[] = [];
  const search = window.location.search;
  if (search?.startsWith('?')) {
    chunks.push(search.slice(1));
  }
  const hash = window.location.hash || '';
  const queryIndex = hash.indexOf('?');
  if (queryIndex !== -1) {
    chunks.push(hash.slice(queryIndex + 1));
  }
  return new URLSearchParams(chunks.join('&'));
}

function optionalNumber(value: unknown): number | undefined {
  const text = String(value ?? '').trim();
  if (!text) return undefined;
  const num = Number(text);
  return Number.isFinite(num) ? num : undefined;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

@Component({
  selector: 'app-customer-activity-capture',
  templateUrl: './customer-activity-capture.component.html',
  styleUrls: ['./customer-activity-capture.component.scss'],
  encapsulation: ViewEncapsulation.None,
  host: { class: 'customer-activity-capture-page' },
})
export class CustomerActivityCaptureComponent implements OnInit, OnDestroy {
  readonly apiUrl = environment.captureCustomerActivity;
  readonly simTabs = SIM_TABS;
  readonly scenariosByTab = SCENARIOS_BY_TAB;
  readonly channel = 'Simulator';

  activeTab: (typeof SIM_TABS)[number]['id'] = 'ev';
  clockDisplay = '--:--:--';

  sidebarTab: 'details' | 'documents' = 'details';
  showSavedBadge = false;
  fileDragOver = false;
  docUploadDragOver = false;
  extracting = false;
  uploadingDocument = false;
  docListLoading = false;
  uploadText = 'Upload invoice / doc to autofill';
  docUploadText = 'Upload any document (PDF, image, etc.)';
  runningAll = false;

  intelligenceLoading = false;
  intelligence: CustomerIntelligenceResponse | null = null;

  clientId: number | null = null;
  centerId: number | null = null;
  activeLeadId: number | string | null = null;

  uploadedDocuments: { id?: number; document_name: string; createdAt?: string }[] = [];

  customerName = '';
  customerPhone = '';
  customerEmail = '';
  countryCode = '';
  formClientId: number | null = null;
  formCenterId: number | null = null;

  customerApplied = false;
  cardStates: Record<string, CardRunState> = {};
  crmFeed: CrmFeedEvent[] = [];
  crmFeedEmpty = true;
  toasts: ToastItem[] = [];

  stats: SimStats = {
    totalSpend: 0,
    totalVisits: 0,
    totalLeads: 0,
    crmEvents: 0,
    apiCalls: 0,
    apiSuccess: 0,
    lastLeadId: '—',
    byType: {
      ev: { count: 0, amount: 0 },
      food: { count: 0, amount: 0 },
      mall: { count: 0, amount: 0 },
      gas: { count: 0, amount: 0 },
      charges: { count: 0, amount: 0 },
      partner: { count: 0, amount: 0 },
    },
  };

  @ViewChild('docFileInput') docFileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('generalDocInput') generalDocInput?: ElementRef<HTMLInputElement>;

  private clockTimer?: ReturnType<typeof setInterval>;
  private toastTimers: ReturnType<typeof setTimeout>[] = [];
  private savedBadgeTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private route: ActivatedRoute,
    private localStorage: LocalStorageService,
    private customer360: Customer360Service,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.applyInitialScope();
    this.updateClock();
    this.clockTimer = setInterval(() => this.updateClock(), 1000);
  }

  ngOnDestroy(): void {
    if (this.clockTimer) clearInterval(this.clockTimer);
    this.toastTimers.forEach((t) => clearTimeout(t));
    if (this.savedBadgeTimer) clearTimeout(this.savedBadgeTimer);
  }

  get avatarInitials(): string {
    if (!this.customerName.trim()) return '?';
    return this.customerName
      .trim()
      .split(/\s+/)
      .map((w) => w[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  }

  get profileMeta(): string {
    if (!this.customerApplied) return 'Set details in the form above';
    const parts: string[] = [];
    if (this.countryCode && this.customerPhone) {
      parts.push(`${this.countryCode} ${this.customerPhone}`);
    } else if (this.customerPhone) {
      parts.push(this.customerPhone);
    }
    if (this.customerEmail) parts.push(this.customerEmail);
    return parts.join(' · ') || 'Customer configured';
  }

  get crmCountLabel(): string {
    const n = this.stats.crmEvents;
    return `${n} event${n !== 1 ? 's' : ''}`;
  }

  get formattedTotalSpend(): string {
    return '₹' + this.stats.totalSpend.toLocaleString('en-IN');
  }

  get formattedAvgTicket(): string {
    const avg = this.stats.totalVisits
      ? Math.round(this.stats.totalSpend / this.stats.totalVisits)
      : 0;
    return '₹' + avg.toLocaleString('en-IN');
  }

  get formattedTotalValue(): string {
    return this.formattedTotalSpend;
  }

  typeCountLabel(type: keyof SimStats['byType'], unit: string): string {
    return `${this.stats.byType[type].count} ${unit}`;
  }

  typeAmount(type: keyof SimStats['byType']): string {
    return '₹' + this.stats.byType[type].amount.toLocaleString('en-IN');
  }

  behaviourPct(type: keyof SimStats['byType']): number {
    const total = this.stats.totalSpend || 1;
    return Math.min(100, Math.round((this.stats.byType[type].amount / total) * 100));
  }

  get freqPct(): number {
    return Math.min(100, this.stats.totalVisits * 8);
  }

  getCardState(id: string): CardRunState {
    if (!this.cardStates[id]) {
      this.cardStates[id] = {
        status: 'idle',
        statusText: 'Idle',
        flowSteps: [],
        payloadJson: null,
        response: null,
        running: false,
      };
    }
    return this.cardStates[id];
  }

  statusClass(status: CardRunState['status']): string {
    return `status-badge status-${status}`;
  }

  switchTab(tab: (typeof SIM_TABS)[number]['id']): void {
    this.activeTab = tab;
  }

  setSidebarTab(tab: 'details' | 'documents'): void {
    this.sidebarTab = tab;
    if (tab === 'documents') {
      this.loadUploadedDocuments();
    }
  }

  refreshCustomerIntelligence(): void {
    if (!this.activeLeadId) return;
    const clientId = this.formClientId ?? this.clientId;
    if (!clientId) return;

    this.intelligenceLoading = true;
    this.customer360.getCustomerIntelligence(this.activeLeadId, clientId).subscribe({
      next: (res) => {
        this.intelligence = res;
        this.intelligenceLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.intelligence = null;
        this.intelligenceLoading = false;
      },
    });
  }

  get intelligenceLayer4() {
    return this.intelligence?.layer4;
  }

  get intelligenceAverageSpend(): string {
    const raw = this.intelligenceLayer4?.average_spend_value as { value?: number } | number | undefined;
    const amount = raw != null && typeof raw === 'object' && 'value' in raw ? raw.value : raw;
    if (amount == null || !Number.isFinite(Number(amount))) return '—';
    return `₹${Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  }

  get canUploadDocument(): boolean {
    return !!(this.customerApplied && this.activeLeadId && (this.formClientId ?? this.clientId));
  }

  triggerGeneralDocSelect(): void {
    if (!this.canUploadDocument) return;
    this.generalDocInput?.nativeElement.click();
  }

  onGeneralDocSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) void this.handleGeneralDocumentUpload(file);
    input.value = '';
  }

  formatDocDate(value: string): string {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-IN');
  }

  loadUploadedDocuments(): void {
    const clientId = optionalNumber(this.formClientId) ?? this.clientId;
    const centerId = optionalNumber(this.formCenterId) ?? this.centerId;
    if (!clientId || !this.activeLeadId) {
      this.uploadedDocuments = [];
      return;
    }

    this.docListLoading = true;
    this.customer360
      .getCustomerDocumentsForScope(this.activeLeadId, clientId, centerId, 0, 20)
      .subscribe({
        next: (res: any) => {
          const rows = res?.rows ?? [];
          this.uploadedDocuments = rows.map((row: any) => ({
            id: row.id,
            document_name: row.document_name || row.file_name || 'Document',
            createdAt: row.createdAt,
          }));
          this.docListLoading = false;
        },
        error: () => {
          this.uploadedDocuments = [];
          this.docListLoading = false;
        },
      });
  }

  async handleGeneralDocumentUpload(file: File): Promise<void> {
    const clientId = optionalNumber(this.formClientId) ?? this.clientId;
    const centerId = optionalNumber(this.formCenterId) ?? this.centerId;

    if (!this.customerApplied) {
      this.showToast('⚠️', 'Apply customer details first', 'Validation');
      this.setSidebarTab('details');
      return;
    }
    if (!clientId) {
      this.showToast('❌', 'Client ID is required before uploading', 'Error');
      return;
    }
    if (!this.activeLeadId) {
      this.showToast('⚠️', 'Run a simulation first to create a lead', 'Validation');
      this.setSidebarTab('documents');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      this.showToast('❌', 'Document must be 4 MB or smaller', 'Error');
      return;
    }

    this.uploadingDocument = true;
    this.docUploadText = `Uploading ${file.name}...`;

    try {
      const fileBase64 = await this.readFileAsBase64(file);
      await firstValueFrom(
        this.customer360.createCustomerDocumentForScope(
          this.activeLeadId,
          clientId,
          centerId,
          {
            document_name: file.name,
            file_base64: fileBase64,
            file_type: file.type || undefined,
            file_size: file.size,
          }
        )
      );
      this.showToast('📎', `${file.name} uploaded`, 'Document');
      this.loadUploadedDocuments();
    } catch (err: any) {
      this.showToast(
        '❌',
        err?.error?.message || 'Failed to upload document',
        'Error'
      );
    } finally {
      this.uploadingDocument = false;
      this.docUploadText = 'Upload any document (PDF, image, etc.)';
    }
  }

  triggerFileSelect(): void {
    this.docFileInput?.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) void this.handleDocumentUpload(file);
    input.value = '';
  }

  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent): void {
    const autofillZone = (event.target as HTMLElement)?.closest?.('.file-upload-zone:not(.doc-upload-zone)');
    const docZone = (event.target as HTMLElement)?.closest?.('.doc-upload-zone');
    if (!autofillZone && !docZone) return;
    event.preventDefault();
    if (docZone) {
      this.docUploadDragOver = true;
    } else {
      this.fileDragOver = true;
    }
  }

  @HostListener('dragleave', ['$event'])
  onDragLeave(event: DragEvent): void {
    const autofillZone = (event.target as HTMLElement)?.closest?.('.file-upload-zone:not(.doc-upload-zone)');
    const docZone = (event.target as HTMLElement)?.closest?.('.doc-upload-zone');
    if (!autofillZone && !docZone) return;
    if (docZone) {
      this.docUploadDragOver = false;
    } else {
      this.fileDragOver = false;
    }
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent): void {
    const autofillZone = (event.target as HTMLElement)?.closest?.('.file-upload-zone:not(.doc-upload-zone)');
    const docZone = (event.target as HTMLElement)?.closest?.('.doc-upload-zone');
    if (docZone) {
      event.preventDefault();
      this.docUploadDragOver = false;
      const file = event.dataTransfer?.files?.[0];
      if (file) void this.handleGeneralDocumentUpload(file);
      return;
    }
    if (!autofillZone) return;
    event.preventDefault();
    this.fileDragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) void this.handleDocumentUpload(file);
  }

  saveCustomerDetails(): void {
    const name = this.customerName.trim();
    const phone = this.customerPhone.trim();
    const email = this.customerEmail.trim();
    const cid = optionalNumber(this.formClientId) ?? this.clientId;
    const ctid = optionalNumber(this.formCenterId) ?? this.centerId;

    if (!name) return;
    if (!phone && !email) return;

    this.customerName = name;
    this.customerPhone = phone;
    this.customerEmail = email;
    this.countryCode = this.countryCode.trim();
    this.clientId = cid ?? null;
    this.centerId = ctid ?? null;
    this.formClientId = this.clientId;
    this.formCenterId = this.centerId;
    this.customerApplied = true;

    this.showSavedBadge = true;
    if (this.savedBadgeTimer) clearTimeout(this.savedBadgeTimer);
    this.savedBadgeTimer = setTimeout(() => (this.showSavedBadge = false), 2200);

    this.showToast('✅', `Customer set: ${name}`, phone || email);
  }

  async handleDocumentUpload(file: File): Promise<void> {
    const clientId = optionalNumber(this.formClientId) ?? this.clientId;
    const centerId = optionalNumber(this.formCenterId) ?? this.centerId;

    if (!clientId) {
      this.showToast('❌', 'Client ID is required before extracting', 'Error');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      this.showToast('❌', 'Document must be 4 MB or smaller', 'Error');
      return;
    }

    this.extracting = true;
    this.uploadText = `Processing ${file.name}...`;

    try {
      const fileBase64 = await this.readFileAsBase64(file);
      const payload: {
        client_id: number;
        center_id?: number;
        file_base64: string;
        file_name: string;
        file_type?: string;
      } = {
        client_id: clientId,
        file_base64: fileBase64,
        file_name: file.name,
        file_type: file.type || undefined,
      };
      if (centerId != null) payload.center_id = centerId;

      const responseData = await firstValueFrom(
        this.customer360.extractCustomerActivityDocument(payload)
      );
      const root = responseData?.data || responseData;
      const extracted = root?.extracted || root;
      const customer = extracted?.customer || extracted || {};

      this.customerName = customer.name || '';
      this.customerPhone = customer.phone || '';
      this.customerEmail = customer.email || '';
      this.countryCode =
        customer.country_code || customer.countryCode || customer.cc || '';

      this.showToast('📝', 'Details extracted and autofilled!', 'Autofill');
      this.saveCustomerDetails();
    } catch (err: any) {
      this.showToast(
        '❌',
        err?.error?.message || 'Failed to extract customer details',
        'Error'
      );
    } finally {
      this.extracting = false;
      this.uploadText = 'Upload invoice / doc to autofill';
    }
  }

  validateCustomerData(): boolean {
    if (
      !this.customerApplied ||
      !this.customerName.trim() ||
      (!this.customerPhone.trim() && !this.customerEmail.trim())
    ) {
      this.showToast(
        '⚠️',
        'Please configure and apply customer details first!',
        'Validation'
      );
      this.setSidebarTab('details');
      return false;
    }
    return true;
  }

  async runScenario(scenario: SimScenario): Promise<void> {
    if (!this.validateCustomerData()) return;

    const state = this.getCardState(scenario.id);
    if (state.running) return;

    state.running = true;
    state.status = 'running';
    state.statusText = '● Running';
    state.flowSteps = [];
    state.payloadJson = null;
    state.response = null;
    this.syncCardState(scenario.id, state);

    const tabKey = scenario.tab;
    const accent = scenario.accentColor;
    const numStyle = this.flowNumStyle(accent);

    try {
      for (let i = 0; i < scenario.eventsData.length; i++) {
        const ev = scenario.eventsData[i];
        await delay(i === 0 ? 600 : 500);

        state.flowSteps = [
          ...state.flowSteps,
          this.makeFlowStep(
            String(i + 1),
            ev.step,
            ev.detail,
            numStyle,
            ev.tag
          ),
        ];
        this.syncCardState(scenario.id, state);

        if (ev.payload) {
          const pl = this.buildPayload(scenario.activityType, ev.payload);
          state.payloadJson = JSON.stringify(pl, null, 2);
          this.syncCardState(scenario.id, state);
          await delay(800);

          state.flowSteps = [
            ...state.flowSteps,
            this.makeFlowStep(
              '→',
              'Sending to CRM API',
              `POST <span>${this.apiUrl}</span>`,
              this.apiSendStyle(accent),
              ''
            ),
          ];
          this.syncCardState(scenario.id, state);
          await delay(1000);

          const started = Date.now();
          const result = await this.callCRMApi(pl);
          const elapsed = Date.now() - started;

          if (result.success) {
            const amount = ev.payload.amount || 0;
            this.stats.totalSpend += amount;
            this.stats.totalVisits++;
            this.stats.totalLeads++;
            this.stats.byType[tabKey].count++;
            this.stats.byType[tabKey].amount += amount;
            this.stats.lastLeadId = String(result.lead_id);
            this.activeLeadId = result.lead_id;
            this.refreshCustomerIntelligence();

            state.response = {
              ok: true,
              msg: `✓ Lead created — Customer ${result.customer_id} · Lead ${result.lead_id}`,
              ms: ev.respTime ?? elapsed,
            };
            this.syncCardState(scenario.id, state);

            this.pushCRMEvent(scenario, ev, result.lead_id);
            this.showToast(
              this.crmIconForTab(tabKey),
              this.toastMsgForScenario(scenario, ev),
              String(result.lead_id)
            );
            this.stats = { ...this.stats };
          } else {
            state.status = 'error';
            state.statusText = 'Error';
            state.response = {
              ok: false,
              msg: result.message || 'API call failed',
              ms: elapsed,
            };
            this.syncCardState(scenario.id, state);
          }
          await delay(500);
        }
        await delay(300);
      }

      if (state.status === 'running') {
        state.status = 'done';
        state.statusText = '✓ Done';
      }
    } catch (err: any) {
      state.status = 'error';
      state.statusText = 'Error';
      state.response = {
        ok: false,
        msg: err?.message || 'Simulation failed',
        ms: 0,
      };
      this.syncCardState(scenario.id, state);
    } finally {
      state.running = false;
      this.syncCardState(scenario.id, state);
    }
  }

  async runAllSimulations(): Promise<void> {
    if (!this.validateCustomerData()) return;
    this.runningAll = true;
    const allScenarios = Object.values(this.scenariosByTab).flat();
    for (let i = 0; i < allScenarios.length; i++) {
      setTimeout(() => {
        const state = this.getCardState(allScenarios[i].id);
        if (!state.running) void this.runScenario(allScenarios[i]);
      }, i * 1800);
    }
    setTimeout(() => (this.runningAll = false), allScenarios.length * 1800 + 2000);
  }

  trustHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private applyInitialScope(): void {
    const params = readUrlQueryParams();
    const routeParams = this.route.snapshot.queryParamMap;

    const clientRaw =
      routeParams.get('client_id') ||
      routeParams.get('clientId') ||
      params.get('client_id') ||
      params.get('clientId') ||
      this.localStorage.getClientId();
    const centerRaw =
      routeParams.get('center_id') ||
      routeParams.get('centerId') ||
      params.get('center_id') ||
      params.get('centerId') ||
      this.localStorage.getCenterId();

    this.clientId = optionalNumber(clientRaw) ?? null;
    this.centerId = optionalNumber(centerRaw) ?? null;
    this.formClientId = this.clientId;
    this.formCenterId = this.centerId;
  }

  private updateClock(): void {
    this.clockDisplay = new Date().toLocaleTimeString('en-IN', { hour12: false });
  }

  private buildPayload(
    activityType: string,
    eventExtras: NonNullable<SimScenario['eventsData'][0]['payload']>
  ) {
    return {
      client_id: this.clientId,
      center_id: this.centerId,
      channel: this.channel,
      activity_type: activityType,
      customer: {
        name: this.customerName.trim(),
        phone: this.customerPhone.trim() || undefined,
        email: this.customerEmail.trim() || undefined,
        country_code: this.countryCode.trim() || undefined,
      },
      event: {
        timestamp: new Date().toISOString(),
        currency: 'INR',
        ...eventExtras,
      },
    };
  }

  private async callCRMApi(payload: ReturnType<CustomerActivityCaptureComponent['buildPayload']>) {
    this.stats.apiCalls++;
    try {
      const data = await firstValueFrom(
        this.customer360.captureCustomerActivity(payload as any)
      );
      if (data?.success) {
        this.stats.apiSuccess++;
        return {
          success: true as const,
          customer_id: data.customer_id,
          lead_id: data.lead_id,
        };
      }
      return {
        success: false as const,
        message: data?.message || 'Capture failed',
      };
    } catch (err: any) {
      return {
        success: false as const,
        message: err?.error?.message || err?.message || 'Network error',
      };
    }
  }

  private makeFlowStep(
    num: string,
    title: string,
    detail: string,
    numStyle: string,
    tag?: string
  ): FlowStep {
    const name = this.customerName.trim() || 'Rajesh Kumar';
    const dynamicDetail = detail.replace(/Rajesh Kumar/g, name);
    const dynamicTitle = title.replace(/Rajesh Kumar/g, name);
    const titleHtml = tag ? `${dynamicTitle}${tag}` : dynamicTitle;
    return {
      num,
      title: this.trustHtml(titleHtml),
      detail: this.trustHtml(dynamicDetail),
      numStyle,
    };
  }

  private syncCardState(id: string, state: CardRunState): void {
    this.cardStates = { ...this.cardStates, [id]: { ...state } };
    this.cdr.detectChanges();
  }

  private flowNumStyle(accent: string): string {
    const map: Record<string, string> = {
      ev: 'background:var(--ev-dim);color:var(--ev)',
      food: 'background:var(--food-dim);color:var(--food)',
      mall: 'background:var(--mall-dim);color:var(--mall)',
      gas: 'background:var(--gas-dim);color:var(--gas)',
      charges: 'background:var(--gas2-dim);color:var(--gas2)',
      partner: 'background:var(--partner-dim);color:var(--partner)',
    };
    return map[accent] || map['ev'];
  }

  private apiSendStyle(accent: string): string {
    const map: Record<string, string> = {
      ev: 'background:rgba(0,212,255,.08);color:var(--ev)',
      food: 'background:rgba(255,107,53,.08);color:var(--food)',
      mall: 'background:rgba(168,85,247,.08);color:var(--mall)',
      gas: 'background:rgba(34,197,94,.08);color:var(--gas)',
      charges: 'background:rgba(245,158,11,.08);color:var(--gas2)',
      partner: 'background:rgba(234,88,12,.08);color:var(--partner)',
    };
    return map[accent] || map['ev'];
  }

  private pushCRMEvent(
    scenario: SimScenario,
    ev: SimScenario['eventsData'][0],
    leadId: string | number
  ): void {
    if (!ev.payload) return;
    const tabKey = scenario.tab;
    const cfg = this.crmEventConfig(tabKey, ev);
    this.crmFeedEmpty = false;
    this.crmFeed = [
      {
        icon: cfg.icon,
        iconClass: cfg.iconClass,
        title: cfg.title,
        sub: cfg.sub,
        amount: cfg.amount,
        amountCol: cfg.amountCol,
        leadId,
        time: new Date().toLocaleTimeString('en-IN', { hour12: false }),
      },
      ...this.crmFeed,
    ];
    this.stats.crmEvents++;
  }

  private crmEventConfig(
    tabKey: SimScenario['tab'],
    ev: SimScenario['eventsData'][0]
  ) {
    const p = ev.payload!;
    const amount = '₹' + p.amount.toLocaleString('en-IN');
    switch (tabKey) {
      case 'ev':
        return {
          icon: '⚡',
          iconClass: 'ev-bg',
          title: 'EV Charge — ' + p.location_name,
          sub: `${p.quantity} kWh · ${p.payment_type}`,
          amount,
          amountCol: 'var(--ev)',
        };
      case 'food':
        return {
          icon: '🍽',
          iconClass: 'food-bg',
          title: 'Food Court — ' + p.location_name,
          sub: `${p.quantity} items · ${p.payment_type}`,
          amount,
          amountCol: 'var(--food)',
        };
      case 'mall':
        return {
          icon: '🛍',
          iconClass: 'mall-bg',
          title: 'Mall Purchase — ' + p.location_name,
          sub: `${p.quantity} item(s) · ${p.payment_type}`,
          amount,
          amountCol: 'var(--mall)',
        };
      case 'gas':
        return {
          icon: '⛽',
          iconClass: 'gas-bg',
          title: 'Gas Refill — ' + p.location_name,
          sub: `${p.quantity}L · ${p.payment_type}`,
          amount,
          amountCol: 'var(--gas)',
        };
      case 'partner':
        return {
          icon: '🛵',
          iconClass: 'partner-bg',
          title: 'Partner Order — ' + p.location_name,
          sub: `${p.quantity} item(s) · ${p.payment_type}`,
          amount,
          amountCol: 'var(--partner)',
        };
      default:
        return {
          icon: '🏬',
          iconClass: 'gas2-bg',
          title: 'Mall Charge — ' + p.location_name,
          sub: p.payment_type,
          amount,
          amountCol: 'var(--gas2)',
        };
    }
  }

  private crmIconForTab(tab: SimScenario['tab']): string {
    return (
      { ev: '⚡', food: '🍽', mall: '🛍', gas: '⛽', charges: '🏬', partner: '🛵' }[tab] || '●'
    );
  }

  private toastMsgForScenario(
    scenario: SimScenario,
    ev: SimScenario['eventsData'][0]
  ): string {
    const p = ev.payload;
    if (!p) return scenario.name;
    switch (scenario.tab) {
      case 'ev':
        return `EV session captured: ${p.location_name}`;
      case 'food':
        return `Food order captured: ₹${p.amount}`;
      case 'mall':
        return `Mall purchase: ${p.location_name}`;
      case 'gas':
        return `Gas refill: ${p.quantity}L · ₹${p.amount}`;
      case 'partner':
        return `Partner order captured: ${p.location_name} · ₹${p.amount}`;
      default:
        return `Mall entry fee captured: ₹${p.amount}`;
    }
  }

  private showToast(icon: string, msg: string, tag: string): void {
    this.toasts = [...this.toasts, { icon, msg, tag }];
    const timer = setTimeout(() => {
      this.toasts = this.toasts.slice(1);
    }, 4200);
    this.toastTimers.push(timer);
  }

  private readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || '');
        resolve(result.includes(',') ? result.split(',')[1] : result);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }
}
